import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/activities", label: "Activities" },
  { to: "/veterans-park", label: "Veterans Park" },
  { to: "/membership", label: "Membership" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // When over dark hero (not scrolled) use WHITE text; when over light bg use dark blue
  const isLight = scrolled;
  const brandText = isLight ? "text-[#0A3161]" : "text-white";
  const tagText = isLight ? "text-[#B22234]" : "text-white";
  const navBase = isLight ? "text-[#0A3161]" : "text-white";
  const navActive = isLight ? "text-[#0A3161] border-b-2 border-[#B22234] pb-1" : "text-white underline underline-offset-[10px] decoration-2";
  const phoneText = isLight ? "text-[#0A3161] hover:text-[#B22234]" : "text-white hover:text-white/80";
  const menuBtn = isLight ? "text-[#0A3161]" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isLight
          ? "bg-white border-b border-[#D9DDE3] shadow-md"
          : "bg-gradient-to-b from-[#0A3161]/85 via-[#0A3161]/60 to-transparent backdrop-blur-[2px]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-14 h-14 rounded-full bg-[#0A3161] flex items-center justify-center border-2 border-white shadow-lg group-hover:rotate-6 transition-transform duration-500">
              <span className="font-display text-white text-xl font-bold">240</span>
            </div>
            <div className="leading-tight">
              <p className={`font-display text-xl md:text-2xl font-bold ${brandText}`}>VVA Chapter 240</p>
              <p className={`text-xs font-condensed uppercase tracking-widest font-semibold ${tagText}`}>
                Sumner County, TN
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`nav-link font-condensed uppercase text-base tracking-widest font-semibold ${
                  location.pathname === l.to ? navActive : navBase
                } hover:opacity-80`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:8885480085"
              className={`flex items-center gap-2 transition-colors ${phoneText}`}
            >
              <Phone className="w-5 h-5" />
              <span className="font-condensed text-base tracking-wide font-semibold">888-548-0085</span>
            </a>
            <Link to="/membership">
              <Button className="bg-[#B22234] hover:bg-[#8f1b2a] text-white rounded-none font-condensed uppercase tracking-widest text-sm font-semibold px-7 h-12">
                Join Chapter 240
              </Button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 ${menuBtn}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#D9DDE3] shadow-lg">
          <nav className="flex flex-col p-6 gap-6">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-condensed uppercase text-lg tracking-widest font-semibold text-[#0A3161] hover:text-[#B22234]"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="tel:8885480085"
              className="flex items-center gap-2 text-[#0A3161] font-condensed text-base font-semibold"
            >
              <Phone className="w-5 h-5" /> 888-548-0085
            </a>
            <Link to="/membership">
              <Button className="w-full bg-[#B22234] hover:bg-[#8f1b2a] text-white rounded-none font-condensed uppercase tracking-widest text-sm font-semibold h-12">
                Join Chapter 240
              </Button>
            </Link>
          </nav>
        </div>
      )}

      <div className="ribbon-divider" />
    </header>
  );
}
